// Apps Setup → handing an app to an outside developer. The admin half issues
// expiring links; the public half is read by the page at /handoff/:token with
// no account at all, so it never sends an Authorization header.
import { api } from './client';
import type {
  ScreenshotContext, ScreenshotContextSwitches, ScreenshotPlan, CopyMode, CopyScreenshotsResult,
  FeatureGraphicInput, FeatureGraphicResult,
} from './play';
import { API_FETCH_URL } from './base';

const BASE_URL = API_FETCH_URL;
// /media/<key> is served from the worker root, one level above /api/v1.
const MEDIA_BASE = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/api\/v1\/?$/, '');

/** Preview URL for a stored screenshot, for the admin side of the feature. */
export const storeAssetUrl = (mediaKey: string) => `${MEDIA_BASE}/media/${mediaKey}`;

export interface StoreAsset {
  id: string;
  app_id: string;
  kind: string;
  media_key: string;
  content_type: string;
  caption: string | null;
  sort_order: number;
  /** Store locale of this frame; 'en-US' for everything made before localization. */
  lang: string;
  created_at: number;
}

export interface HandoffLink {
  id: string;
  app_id: string;
  build_id: string | null;
  note: string | null;
  expires_at: number;
  created_at: number;
  last_used_at: number | null;
  view_count: number;
  download_count: number;
  revoked_at: number | null;
  build_type: string | null;
  version_name: string | null;
  version_code: number | null;
  created_by_name: string | null;
  /** Outside publisher the link was issued to, when one was chosen. */
  publisher_name: string | null;
}

/** Returned once, at creation: only the token's hash is stored server-side. */
export interface IssuedHandoffLink {
  id: string;
  token: string;
  url: string;
  expires_at: number;
  created_at: number;
}

export const getStoreAssets = (appId: string) =>
  api.get<StoreAsset[]>(`/admin/apps-setup/store-assets/${appId}`);

export const attachStoreAssets = (appId: string, mediaKeys: string[]) =>
  api.post<{ added: number }>(`/admin/apps-setup/store-assets/${appId}`, { media_keys: mediaKeys });

export const deleteStoreAsset = (appId: string, assetId: string) =>
  api.delete<null>(`/admin/apps-setup/store-assets/${appId}/${assetId}`);

/** Uploads one screenshot into one locale's set; the server caps each locale at 8. */
export function uploadStoreAsset(appId: string, file: File, lang: string): Promise<{ id: string; media_key: string }> {
  return api.upload<{ id: string; media_key: string }>(
    `/admin/apps-setup/store-assets/${appId}/upload?lang=${encodeURIComponent(lang)}`, file, file.type,
  );
}

/** Uploads the app's one feature graphic (1024×500); it replaces the previous one. */
export function uploadFeatureGraphic(appId: string, file: File): Promise<{ id: string; media_key: string }> {
  return api.upload<{ id: string; media_key: string }>(
    `/admin/apps-setup/store-assets/${appId}/upload?kind=feature`, file, file.type,
  );
}

/**
 * Puts one locale's screenshots into the given order. `ids` must be that
 * locale's whole set; the server refuses a partial list.
 */
export const reorderStoreAssets = (appId: string, lang: string, ids: string[]) =>
  api.put<null>(`/admin/apps-setup/store-assets/${appId}/order`, { lang, ids });

// ---- generating and copying a locale's screenshots ------------------------

export const getStoreScreenshotContext = (appId: string) =>
  api.get<ScreenshotContext>(`/admin/apps-setup/store-assets/${appId}/screenshot-context`);

export const planStoreScreenshots = (appId: string, input: {
  lang: string; count: number; brief: string;
} & Partial<ScreenshotContextSwitches>) =>
  api.post<ScreenshotPlan>(`/admin/apps-setup/store-assets/${appId}/screenshot-plan`, input);

/** Draws one frame and attaches it to the locale right away. */
export const generateStoreScreenshot = (appId: string, input: {
  lang: string; style: string; scene: string; caption: string; index: number; total: number; subject: string;
} & Partial<ScreenshotContextSwitches>) =>
  api.post<{ id: string; media_key: string; url: string; contentType: string }>(
    `/admin/apps-setup/store-assets/${appId}/generate-screenshot`, input,
  );

/** Draws the app's feature graphic from the source listing and attaches it, replacing the current one. */
export const generateStoreFeatureGraphic = (appId: string, input: FeatureGraphicInput & Partial<ScreenshotContextSwitches>) =>
  api.post<FeatureGraphicResult & { media_key: string }>(
    `/admin/apps-setup/store-assets/${appId}/generate-feature-graphic`, input,
  );

/** One locale's frames into other locales (null = every locale), sharing the stored files. */
export const copyStoreScreenshots = (appId: string, input: { sourceLang: string; targetLangs: string[] | null; mode: CopyMode }) =>
  api.post<CopyScreenshotsResult>(`/admin/apps-setup/store-assets/${appId}/copy`, {
    source_lang: input.sourceLang, mode: input.mode,
    ...(input.targetLangs ? { target_langs: input.targetLangs } : { all: true }),
  });

/** Replaces the app icon (512×512 PNG/JPEG); the new URL lands on the app row. */
export function uploadStoreIcon(appId: string, file: File): Promise<{ icon_url: string }> {
  return api.upload<{ icon_url: string }>(`/admin/apps-setup/store-icon/${appId}`, file, file.type);
}

export const removeStoreIcon = (appId: string) =>
  api.delete<null>(`/admin/apps-setup/store-icon/${appId}`);

// ---- store listing text ---------------------------------------------------
// The listing package the handoff page serves. `HandoffListing` below is the
// same row shape the public page reads, so both halves stay in step.

export const getStoreListings = (appId: string) =>
  api.get<HandoffListing[]>(`/admin/apps-setup/store-listings/${appId}`);

export const saveStoreListing = (
  appId: string,
  lang: string,
  input: { title: string; short_description: string; full_description: string },
) => api.put<HandoffListing>(
  `/admin/apps-setup/store-listings/${appId}/${encodeURIComponent(lang)}`, input,
);

/**
 * Transcreate one locale from another (OpenAI, same path as the wizard). The
 * result is returned for review — saving it is a separate PUT.
 */
export const translateStoreListing = (appId: string, lang: string, from?: string) =>
  api.post<HandoffListing & { source_lang: string }>(
    `/admin/apps-setup/store-listings/${appId}/${encodeURIComponent(lang)}/translate`,
    from ? { from } : {},
  );

export const deleteStoreListing = (appId: string, lang: string) =>
  api.delete<null>(`/admin/apps-setup/store-listings/${appId}/${encodeURIComponent(lang)}`);

export const getHandoffLinks = (appId: string) =>
  api.get<HandoffLink[]>(`/admin/apps-setup/handoff-links/${appId}`);

export const createHandoffLink = (input: {
  app_id: string; build_id?: string; ttl_hours: number; note?: string;
  /** Who will host the app, with the price agreed for this app (cents). */
  publisher_id?: string; publish_cents?: number; rent_cents?: number;
}) => api.post<IssuedHandoffLink>('/admin/apps-setup/handoff-links', input);

/**
 * Points a live link at another build, so a rebuilt app is served by the URL
 * the receiver already has. The rest of the package is read live from the app.
 */
export const repointHandoffLink = (id: string, buildId: string) =>
  api.post<{ id: string; build_id: string }>(
    `/admin/apps-setup/handoff-links/${id}/build`, { build_id: buildId },
  );

export const revokeHandoffLink = (id: string) =>
  api.post<null>(`/admin/apps-setup/handoff-links/${id}/revoke`, {});

// ---- public side ----------------------------------------------------------

export interface HandoffPackage {
  app: {
    id: string;
    name: string;
    platform: string;
    bundle_id: string | null;
    icon_url: string | null;
    short_description: string | null;
    full_description: string | null;
    privacy_policy_url: string | null;
    contact_email: string | null;
  };
  build: {
    id: string;
    build_type: string;
    version_name: string;
    version_code: number;
    created_at: number;
    filename: string;
  } | null;
  has_icon: boolean;
  /** Play's 1024x500 banner, when the app has one. */
  has_feature_graphic: boolean;
  /** A Data safety CSV to import in the Play Console (Android apps only). */
  has_data_safety_csv: boolean;
  /**
   * The handoff template's answers for the console forms, raw JSON — read with
   * lib/play-template. Android only; null when no template is marked.
   */
  answers: { app_content: string; content_rating: string; listing_defaults: string } | null;
  /** Every locale of the listing, source first. Never empty. */
  listings: HandoffListing[];
  screenshots: { id: string; caption: string | null; content_type: string; lang: string }[];
  note: string | null;
  expires_at: number;
}

export interface HandoffListing {
  lang: string;
  title: string;
  short_description: string;
  full_description: string;
  is_source: boolean;
  /** false — the text is over Play's limits and needs a trim before it is accepted. */
  fits: boolean;
}

export const getHandoffPackage = (token: string) =>
  api.get<HandoffPackage>(`/handoff/${token}`, { skipAuth: true });

/** Direct file URLs — the browser downloads these itself, no token header involved. */
export const handoffFileUrl = {
  icon: (token: string) => `${BASE_URL}/handoff/${token}/icon`,
  screenshot: (token: string, assetId: string) => `${BASE_URL}/handoff/${token}/screenshot/${assetId}`,
  featureGraphic: (token: string) => `${BASE_URL}/handoff/${token}/feature-graphic`,
  build: (token: string) => `${BASE_URL}/handoff/${token}/build`,
  dataSafetyCsv: (token: string) => `${BASE_URL}/handoff/${token}/data-safety.csv`,
};
